<script setup lang="ts">
import { h, Fragment, render } from 'vue';
import '@algolia/autocomplete-theme-classic';

import { autocomplete, getAlgoliaResults } from '@algolia/autocomplete-js';
import * as algoliaAutocompleteRecentSearchesPkg from '@algolia/autocomplete-plugin-recent-searches';
import { useAlgolia } from '~/composables/useAlgolia';

const { mountAlgoliaInsights } = useAlgolia();

const {
  public: { algoliaIndex }
} = useRuntimeConfig();

const { userData } = useAuth();
const userId = userData.value?.id;

const STALL_THRESHOLD_MS = 500; // For example, 500ms for a 200ms debounce delay
const DEBOUNCE_MS = 200;

const { createLocalStorageRecentSearchesPlugin } = algoliaAutocompleteRecentSearchesPkg;

const client = useAlgoliaSearchClient();
const router = useRouter();
const { initializeAlgolia, getFilters } = useAlgoliaLoading();

// Initialize dealer content for exclusion filters
onMounted(async () => {
  await initializeAlgolia();

  mountAlgoliaInsights()
});

const debouncePromise = (fn: any, time: any) => {
  let timer: any = undefined;

  return function debounced(...args: any[]) {
    if (timer) {
      clearTimeout(timer); // Clear the timeout first if it's already defined.
    }

    return new Promise(resolve => {
      timer = setTimeout(() => resolve(fn(...args)), time);
    });
  };
};

const SearchItemWrapper = defineComponent({
  props: ['item'],
  setup(props) {
    const handleClick = (e: { preventDefault: () => void }) => {
      e.preventDefault();
      router.push(props.item.path);
    };

    return () =>
      h(
        'a',
        {
          href: props.item.path,
          class: 'flex flex-row gap-3 text-stormcloud',
          onClick: handleClick,
          tabindex: 0,
          role: 'link'
        },
        [
          h('img', {
            class: 'h-16 w-16 border-1 border-gainsboro object-cover',
            src: props.item.image
              ? props.item.image + '?height=64&width=64&mode=crop&format=webp'
              : '/images/placeholder.png',
            alt: props.item.name,
            height: 64,
            width: 64
          }),
          h('div', { class: 'flex flex-col pt-0.5' }, [
            h(
              'span',
              { class: 'text-sm font-normal' },
              `${props.item.sku} ${props.item.legacySku ? `(${props.item.legacySku})` : ''}`
            ),
            h('span', { class: 'text-lg font-semibold' }, props.item.name)
          ])
        ]
      );
  }
});

let autocompleteSetQuery: (query: string) => void = () => {};

onMounted(() => {
  const debounced = debouncePromise((items: any) => Promise.resolve(items), DEBOUNCE_MS);

  const recentSearchesPlugin = createLocalStorageRecentSearchesPlugin({
    key: 'navbar',
    limit: 3
  });

  // Get dealer content exclusion filters
  const filters = getFilters();

  const { setQuery } = autocomplete({
    container: '#autocomplete',
    stallThreshold: STALL_THRESHOLD_MS,
    openOnFocus: true,
    plugins: [recentSearchesPlugin],
    placeholder: 'Search',
    insights: true,
    getSources({ query }) {
      return debounced([
        {
          sourceId: 'products',
          getItemUrl({ item }: { item: any }) {
            return item.path;
          },
          getItems() {
            return getAlgoliaResults({
              searchClient: client,
              queries: [
                {
                  indexName: algoliaIndex,
                  params: {
                    query,
                    filters, // Apply dealer content exclusion filters
                    userToken: userId
                  }
                }
              ]
            });
          },
          templates: {
            item({ item }: { item: any }) {
              return h(SearchItemWrapper, { item });
            }
          }
        }
      ]);
    },
    navigator: {
      navigate({ itemUrl }) {
        navigateTo(itemUrl);
      }
    },
    renderer: {
      createElement,
      Fragment,
      render
    },
    render({ children }, root) {
      render(children, root);
    },
    onSubmit({ state }) {
      navigateTo(`/search?q=${state.query}`);
    }
  });

  autocompleteSetQuery = setQuery;
});

const route = useRoute();
// Watch for route changes and clear the query
watch(
  () => route.fullPath,
  newPath => {
    // Only clear the query if the new path doesn't include '/search'
    if (!newPath.includes('/search')) {
      autocompleteSetQuery('');
    }
  }
);
</script>

<template>
  <div id="autocomplete" />
</template>

<style>
/* Desktop styles */
.aa-Autocomplete {
  @apply relative md:z-40;
}

.aa-Form {
  @apply z-10 rounded-full border-1 border-background border-gainsboro bg-gainsboro-50 focus-within:border-tangerine focus-within:shadow-none;
}

.aa-Input::placeholder {
  @apply text-stormcloud;
}

.aa-SubmitButton,
.aa-ClearButton {
  @apply flex items-center justify-center rounded-full;
}
.aa-SubmitIcon,
.aa-ClearIcon,
.aa-ItemIcon {
  @apply fill-[#515C64] text-stormcloud;
}

.aa-ItemActionButton {
  @apply fill-[#515C64] text-stormcloud;
}

.aa-Panel {
  @apply -top-8 z-30 rounded-xl pt-5 shadow-md md:!top-12 md:mt-0;
}

.aa-List:first {
  @apply border-b-2 border-gainsboro pb-2;
}

.aa-List {
  @apply pt-2;
}

.aa-Source:nth-child(2) {
  @apply border-t-[1px] border-gainsboro-50 pt-2;
}

.aa-Source:first-child {
  @apply pb-2 pt-2;
}

/* Mobile styles */
.aa-DetachedContainer {
  @apply top-0 rounded-t-none;
}

.aa-DetachedFormContainer {
  @apply z-40 border-b-0 px-5 pt-5 md:z-30;
}

.aa-DetachedSearchButton {
  @apply ml-auto w-fit justify-end border-0 p-0;
}

.aa-DetachedSearchButtonIcon {
  @apply justify-end;
}

.aa-DetachedSearchButtonPlaceholder,
.aa-DetachedSearchButtonQuery {
  @apply hidden;
}

.aa-PanelLayout {
  @apply px-4;
}
</style>
